import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Minus, Plus } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Button } from '@shared/ui/primitives/Button';
import { type Facility, type FacilityAvailability, type FacilityBooking, type FacilityBookingStatus } from './api';

const DAY_FMT = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

export const STATUS_LABEL: Record<FacilityBookingStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

export const STATUS_TONE: Record<FacilityBookingStatus, string> = {
  pending: 'bg-warning/15 text-warning',
  confirmed: 'bg-primary-50 text-primary-700',
  approved: 'bg-primary-50 text-primary-700',
  rejected: 'bg-danger/15 text-danger',
  cancelled: 'bg-sunken text-ink-muted',
  completed: 'bg-sunken text-ink-secondary',
};

export interface BookingSheetSlot {
  id: string;
  starts_at: string;
  ends_at: string;
  availability?: FacilityAvailability;
  booking?: FacilityBooking;
}

function cleanText(value?: string | null): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function facilityLocationLines(facility: Facility): string[] {
  const baseLines = [
    cleanText(facility.location_name),
    cleanText(facility.location_address),
    [facility.location_city, facility.location_region, facility.location_postal_code]
      .map(cleanText)
      .filter((part): part is string => !!part)
      .join(', '),
  ].filter((line): line is string => !!line);
  const countryCode = cleanText(facility.location_country_code);
  const lines = countryCode && baseLines.length > 0 ? [...baseLines, countryCode] : baseLines;

  return lines.filter((line, index) => {
    const normalized = line.toLowerCase();
    return lines.findIndex((candidate) => candidate.toLowerCase() === normalized) === index;
  });
}

function parseCoordinate(value?: string | number | null): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function facilityMapUrl(facility: Facility): string | null {
  const latitude = parseCoordinate(facility.location_latitude);
  const longitude = parseCoordinate(facility.location_longitude);

  if (latitude === null || longitude === null) return null;

  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

async function openMap(url: string): Promise<void> {
  await Browser.open({ url }).catch(() => {
    window.open(url, '_blank');
  });
}

export function BookingDetailSheet({
  open,
  facility,
  slot,
  confirmLoading,
  cancelLoading,
  clearLoading,
  onOpenChange,
  onConfirm,
  onCancel,
  onClear,
}: {
  open: boolean;
  facility: Facility;
  slot: BookingSheetSlot | null;
  confirmLoading: boolean;
  cancelLoading?: boolean;
  clearLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (slot: BookingSheetSlot, attendeeCount?: number | null) => void;
  onCancel?: (booking: FacilityBooking) => void;
  onClear?: (booking: FacilityBooking) => void;
}): JSX.Element | null {
  const maxAttendees = facility.max_booking_attendees ?? 1;
  const canSelectAttendees = maxAttendees > 1;
  const [renderedSlot, setRenderedSlot] = useState<BookingSheetSlot | null>(slot);
  const [attendeeCount, setAttendeeCount] = useState(1);
  const activeSlot = slot ?? renderedSlot;

  useEffect(() => {
    if (slot) {
      setRenderedSlot(slot);
    }
  }, [slot]);

  useEffect(() => {
    if (!open) return;
    setAttendeeCount(1);
  }, [open, slot?.id, maxAttendees]);

  if (!activeSlot) return null;

  const booking = activeSlot.booking;
  const canConfirm = !!activeSlot.availability && !booking;
  const canCancel =
    !!booking &&
    ['pending', 'confirmed', 'approved'].includes(booking.status) &&
    new Date(activeSlot.starts_at).getTime() > Date.now();
  const canClear = !!booking && booking.status === 'rejected';
  const statusLabel = booking
    ? STATUS_LABEL[booking.status]
    : facility.require_approval
      ? 'Needs estate approval'
      : 'Ready to confirm';
  const startsAt = new Date(activeSlot.starts_at);
  const endsAt = new Date(activeSlot.ends_at);
  const locationLines = facilityLocationLines(facility);
  const mapUrl = facilityMapUrl(facility);
  const attendeesValue = booking?.attendee_count
    ? attendeeLabel(booking.attendee_count)
    : facility.max_booking_attendees
      ? `Up to ${attendeeLabel(facility.max_booking_attendees)}`
      : 'Estate default';

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      onExitComplete={() => {
        if (!open) {
          setRenderedSlot(null);
        }
      }}
      title={booking ? 'Booking details' : facility.require_approval ? 'Request booking' : 'Confirm booking'}
    >
      <div className="space-y-4 pb-4">
        <div className="rounded-xl bg-sunken px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="eyebrow">Facility</p>
              <p className="mt-1 font-display text-headline text-ink">{facility.name}</p>
            </div>
            <span
              className={
                'shrink-0 rounded-pill px-2.5 py-1 text-caption font-semibold ' +
                (booking ? STATUS_TONE[booking.status] : 'bg-primary-50 text-primary-700')
              }
            >
              {statusLabel}
            </span>
          </div>
        </div>

        {locationLines.length > 0 || mapUrl ? (
          <div className="rounded-xl border border-hairline bg-card px-4 py-3">
            <p className="eyebrow mb-1.5">Location</p>
            <div className="space-y-0.5">
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 flex-1 text-body-sm text-ink-secondary">
                  {locationLines[0] ?? 'Pinned location'}
                </p>
                {mapUrl ? (
                  <button
                    type="button"
                    onClick={() => void openMap(mapUrl)}
                    className="shrink-0 text-caption font-medium text-primary-700"
                  >
                    view on map
                  </button>
                ) : null}
              </div>
              {locationLines.slice(1).map((line) => (
                <p key={line} className="text-body-sm text-ink-secondary">
                  {line}
                </p>
              ))}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2.5">
          <InfoTile label="Date" value={DAY_FMT.format(startsAt)} />
          <InfoTile
            label="Time"
            value={`${TIME_FMT.format(startsAt)}-${TIME_FMT.format(endsAt)}`}
          />
          <InfoTile label="Attendees" value={attendeesValue} />
          <InfoTile
            label="Confirmation"
            value={booking?.approval_required || facility.require_approval ? 'After estate approval' : 'Instant'}
          />
        </div>

        {canConfirm && canSelectAttendees ? (
          <div className="rounded-xl border border-hairline bg-card px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="eyebrow mb-1">Attendees</p>
                <p className="text-body-sm font-semibold text-ink">
                  {attendeeLabel(attendeeCount)} selected
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  haptic={false}
                  aria-label="Decrease attendees"
                  disabled={attendeeCount <= 1}
                  onClick={() => setAttendeeCount((value) => Math.max(1, value - 1))}
                >
                  <Minus className="h-4 w-4" strokeWidth={2} />
                </Button>
                <span className="flex h-11 min-w-12 items-center justify-center rounded-sm bg-sunken px-3 text-body font-semibold text-ink">
                  {attendeeCount}
                </span>
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  haptic={false}
                  aria-label="Increase attendees"
                  disabled={attendeeCount >= maxAttendees}
                  onClick={() => setAttendeeCount((value) => Math.min(maxAttendees, value + 1))}
                >
                  <Plus className="h-4 w-4" strokeWidth={2} />
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {facility.description ? (
          <div>
            <p className="eyebrow mb-1.5">About</p>
            <p className="whitespace-pre-line text-body-sm text-ink-secondary">
              {facility.description}
            </p>
          </div>
        ) : null}

        {booking ? (
          <div className="rounded-xl border border-hairline bg-card px-4 py-3">
            <p className="eyebrow mb-1.5">Your booking</p>
            <p className="text-body-sm text-ink-secondary">
              This slot is currently {STATUS_LABEL[booking.status].toLowerCase()}.
              {booking.status === 'pending'
                ? ' The estate team still needs to approve it.'
                : booking.status === 'approved' || booking.status === 'confirmed'
                  ? ' You can see and manage it under My bookings.'
                  : booking.status === 'rejected'
                    ? ' You can clear it from your bookings list once you have seen it.'
                  : null}
            </p>
          </div>
        ) : null}

        {canConfirm ? (
          <Button
            width="full"
            size="lg"
            loading={confirmLoading}
            onClick={() => onConfirm(activeSlot, canSelectAttendees ? attendeeCount : null)}
          >
            {facility.require_approval ? 'Confirm request' : 'Confirm booking'}
          </Button>
        ) : canCancel && booking && onCancel ? (
          <div className="space-y-2">
            <Button
              width="full"
              size="lg"
              variant="danger"
              loading={cancelLoading}
              onClick={() => onCancel(booking)}
            >
              Cancel booking
            </Button>
            <Button width="full" size="lg" variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : canClear && booking && onClear ? (
          <div className="space-y-2">
            <Button
              width="full"
              size="lg"
              variant="secondary"
              loading={clearLoading}
              onClick={() => onClear(booking)}
            >
              Clear
            </Button>
            <Button width="full" size="lg" variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <Button width="full" size="lg" variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        )}
      </div>
    </BottomSheet>
  );
}

function attendeeLabel(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

function InfoTile({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-hairline bg-card px-3 py-3">
      <p className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">
        {label}
      </p>
      <p className="mt-1 text-body-sm font-semibold text-ink">{value}</p>
    </div>
  );
}
